import type { FindingModel, RectModel } from '@/types/documents.types';
import { DEFAULT_COLORS, type DeviceBox, type PaperAndInk, readPaperAndInk } from '@/utils/canvasColors';
import type { PixelRect } from '@/utils/geometry';
import { eraseGlyphs, parseHex } from '@/utils/glyphEraser';
import {
  FALLBACK_SUBSTITUTE_FONT,
  type SubstituteFont,
  embeddedSubstituteFont,
  substituteFont,
} from '@/utils/substituteFont';
import { type TextRun, coverRect, runForRect, runsForRect, textRuns } from '@/utils/substituteLayout';
import { type SubstituteLine, placeSubstituteLines } from '@/utils/substituteLines';
import { measureSubstitute } from '@/utils/textMeasure';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import type { RefObject } from 'react';
import { useEffect, useMemo, useState } from 'react';

/** What goes over one line of a finding in the anonymized preview. */
export interface CoverBox extends PaperAndInk {
  /** Where the cover goes, in CSS pixels on the page; snapped to whole canvas pixels when `image` is set. */
  box: PixelRect;
  /**
   * The page's own pixels under the box with the text painted out (`utils/glyphEraser`), as a PNG
   * data URL; absent while no painted canvas can be read, when the flat `paper` stands in.
   */
  image?: string;
}

/** How the anonymized preview draws one finding. */
export interface FindingPreview {
  /** One cover per finding rectangle, in the order of `rects`. */
  covers: CoverBox[];
  /**
   * Per finding rectangle, the pieces of the substitute that line shows — one per table column it
   * crosses — and where they go; empty for a line without a part, and for every line of a finding
   * without a substitute.
   */
  lines: SubstituteLine[][];
}

/** The scale of the canvas's last completed paint, and a counter that moves with every paint. */
export interface PaintedCanvas {
  factor: number;
  version: number;
}

interface SubstitutePreviewInput {
  /** Only the anonymized mode needs any of this; everywhere else the hook does no work at all. */
  enabled: boolean;
  pdf: PDFDocumentProxy | null;
  pageNumber: number;
  findings: FindingModel[];
  canvasRef: RefObject<HTMLCanvasElement | null>;
  /** Pixels per PDF point the overlays are drawn at. */
  factor: number;
  painted: PaintedCanvas;
}

/** pdf.js's store of a document's fonts (`PDFPageProxy.commonObjs`), by the handle a text item names. */
interface FontObjects {
  has(id: string): boolean;
  get(id: string): unknown;
}

/** The part of a pdf.js font object (`FontFaceObject`) read here. */
interface PdfFont {
  name?: unknown;
  /** Set once pdf.js has added the font to `document.fonts` under the run's `fontName`. */
  attached?: boolean;
  missingFile?: boolean;
  disableFontFace?: boolean;
  isType3Font?: boolean;
}

/**
 * The font to set a run's substitutes in: the document's own embedded font once pdf.js has loaded
 * it for the paint, with the installed look-alike by the PDF's font name behind it; by pdf.js's
 * family class until then.
 */
const fontOfRun = (run: TextRun, fonts: FontObjects | null): SubstituteFont => {
  let font: PdfFont | undefined;
  try {
    if (fonts && run.fontName && fonts.has(run.fontName)) font = fonts.get(run.fontName) as PdfFont;
  } catch {
    font = undefined;
  }
  const lookAlike = substituteFont(typeof font?.name === 'string' ? font.name : undefined, run.genericFamily);
  const embedded = font?.attached && !font.missingFile && !font.disableFontFace && !font.isType3Font;
  return embedded && run.fontName ? embeddedSubstituteFont(run.fontName, lookAlike) : lookAlike;
};

const NO_PREVIEWS = new Map<string, FindingPreview>();

/**
 * The cover area of a rectangle in CSS pixels, before snapping. Deliberately unpadded: growing it
 * to whole canvas pixels already takes the glyphs' antialiased edges, and any more reaches the
 * comma after a name or the accents of the next line, which the export leaves on the page.
 */
const cssBox = (area: RectModel, factor: number): PixelRect => ({
  left: area.x0 * factor,
  top: area.y0 * factor,
  width: Math.max(area.x1 - area.x0, 0) * factor,
  height: Math.max(area.y1 - area.y0, 0) * factor,
});

/** A CSS box grown to whole canvas pixels and kept inside the canvas. */
const snapToCanvas = (box: PixelRect, pixelRatio: number, canvas: HTMLCanvasElement): DeviceBox => {
  const left = Math.max(Math.floor(box.left * pixelRatio), 0);
  const top = Math.max(Math.floor(box.top * pixelRatio), 0);
  const right = Math.min(Math.ceil((box.left + box.width) * pixelRatio), canvas.width);
  const bottom = Math.min(Math.ceil((box.top + box.height) * pixelRatio), canvas.height);
  return { left, top, width: Math.max(right - left, 0), height: Math.max(bottom - top, 0) };
};

/** The canvas pixels under `device` with the glyphs painted out, as a PNG data URL. */
const paintOut = (context: CanvasRenderingContext2D, device: DeviceBox, colors: PaperAndInk): string | undefined => {
  if (device.width <= 0 || device.height <= 0) return undefined;
  try {
    const { data } = context.getImageData(device.left, device.top, device.width, device.height);
    const erased = eraseGlyphs(data, device.width, device.height, parseHex(colors.paper), parseHex(colors.ink));
    const patch = document.createElement('canvas');
    patch.width = device.width;
    patch.height = device.height;
    const patchContext = patch.getContext('2d');
    if (!patchContext) return undefined;
    patchContext.putImageData(new ImageData(erased, device.width, device.height), 0, 0);
    return patch.toDataURL('image/png');
  } catch {
    // A tainted or lost canvas: the flat paper colour covers the glyphs instead.
    return undefined;
  }
};

/**
 * Everything the anonymized preview needs to draw the page's findings the way the export will.
 *
 * Two sources, read when the mode is on: the page's pdf.js text content, for the font, font size,
 * baseline, horizontal scale and height of the run each finding replaces
 * (`utils/substituteLayout`), and the painted canvas, for the colours around and inside each cover
 * (`utils/canvasColors`) and for the pixels the glyphs are painted out of (`utils/glyphEraser`).
 * The canvas is read only from a paint at the current scale, so a zoom in progress falls back to
 * flat white covers for the moment rather than sampling the previous picture at the wrong offsets.
 */
export const useSubstitutePreview = ({
  enabled,
  pdf,
  pageNumber,
  findings,
  canvasRef,
  factor,
  painted,
}: SubstitutePreviewInput): Map<string, FindingPreview> => {
  const [pageRuns, setPageRuns] = useState<{
    pageNumber: number;
    runs: TextRun[];
    fonts: FontObjects | null;
  } | null>(null);

  useEffect(() => {
    if (!enabled || !pdf) return;
    let cancelled = false;
    const load = async () => {
      try {
        const pdfPage = await pdf.getPage(pageNumber);
        const content = await pdfPage.getTextContent();
        if (!cancelled) {
          const runs = textRuns(content.items, pdfPage.getViewport({ scale: 1 }), content.styles);
          setPageRuns({ pageNumber, runs, fonts: pdfPage.commonObjs ?? null });
        }
      } catch {
        // Without text content the preview only loses the exact font sizes; the rectangles still
        // place every substitute.
        if (!cancelled) setPageRuns({ pageNumber, runs: [], fonts: null });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [enabled, pdf, pageNumber]);

  return useMemo(() => {
    if (!enabled) return NO_PREVIEWS;
    const current = pageRuns?.pageNumber === pageNumber ? pageRuns : null;
    // Read again after every paint (`painted`), which is when pdf.js has resolved the page's fonts.
    const runs = (current?.runs ?? []).map((run) => ({ ...run, font: fontOfRun(run, current?.fonts ?? null) }));
    const canvas = canvasRef.current;
    const context = canvas && painted.version > 0 && painted.factor === factor ? canvas.getContext('2d') : null;
    // The canvas is `devicePixelRatio` times denser than the CSS box the rectangles are scaled into.
    const cssWidth = canvas ? Number.parseFloat(canvas.style.width) || canvas.width : 0;
    const pixelRatio = canvas && cssWidth > 0 ? canvas.width / cssWidth : 1;

    const coverOf = (rect: RectModel): CoverBox => {
      const box = cssBox(coverRect(rect, runForRect(rect, runs)), factor);
      if (!context || !canvas) return { box, ...DEFAULT_COLORS };
      const device = snapToCanvas(box, pixelRatio, canvas);
      const colors = readPaperAndInk(context, device, canvas.width, canvas.height);
      return {
        box: {
          left: device.left / pixelRatio,
          top: device.top / pixelRatio,
          width: device.width / pixelRatio,
          height: device.height / pixelRatio,
        },
        ...colors,
        image: paintOut(context, device, colors),
      };
    };

    const previews = new Map<string, FindingPreview>();
    const lineRunsOf = (rect: RectModel) => runsForRect(rect, runs);
    // The export sets these in its bundled DejaVu Sans, at the run's size and position.
    const fallbackRunsOf = (rect: RectModel) =>
      lineRunsOf(rect).map((run) => ({ ...run, font: FALLBACK_SUBSTITUTE_FONT }));
    for (const finding of findings) {
      if (finding.rects.length === 0) continue;
      const runsOf = finding.substitute_font_fallback ? fallbackRunsOf : lineRunsOf;
      previews.set(finding.id, {
        covers: finding.rects.map(coverOf),
        lines: finding.substitute
          ? placeSubstituteLines(finding.substitute, finding.text, finding.rects, runsOf, measureSubstitute)
          : finding.rects.map(() => []),
      });
    }
    return previews;
  }, [enabled, pageRuns, pageNumber, findings, canvasRef, painted, factor]);
};
